import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { tableRows } from "../../utils/xlsx";
import { ACCOUNTING, cellRight, download, EmptyState, ExportButton, LoadingOverlay, money, sheetHeading } from "./common";

const emptyForm = {
  name: "",
  group: "",
  openingBalance: "",
  openingBalanceSide: "DR",
  gstin: "",
  creditDays: "",
  bankName: "",
  bankAccountNumber: "",
  ifscCode: "",
  description: "",
  active: true,
};

const KINDS = [
  { key: "", label: "All ledgers" },
  { key: "user", label: "Created by you" },
  { key: "system", label: "System ledgers" },
  { key: "CUSTOMER", label: "Customers" },
  { key: "SUPPLIER", label: "Suppliers" },
];

const kindOf = (ledger) => (ledger.systemKey ? "system" : ledger.partyType || "user");
const kindLabel = (ledger) => (ledger.systemKey ? "System" : ledger.partyType === "CUSTOMER" ? "Customer" : ledger.partyType === "SUPPLIER" ? "Supplier" : "User");
const BANK_GROUPS = ["BANK_ACCOUNTS", "BANK_OD"];

/**
 * Chart of accounts. Customer, supplier and system ledgers (cash, bank, sales, purchase, GST…) are created automatically;
 * add your own for expenses, incomes, capital, loans, fixed assets and extra bank accounts.
 */
const Ledgers = () => {
  const [ledgers, setLedgers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [group, setGroup] = useState("");
  const [kind, setKind] = useState("");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ledgerData, groupData] = await Promise.all([api.get(`${ACCOUNTING}/ledgers`, { includeInactive: true }), api.get(`${ACCOUNTING}/groups`)]);
      setLedgers(Array.isArray(ledgerData) ? ledgerData : []);
      setGroups(Array.isArray(groupData) ? groupData : []);
    } catch (error) {
      toast.error(error.message || "Failed to load ledgers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const groupByCode = useMemo(() => Object.fromEntries(groups.map((g) => [g.code, g])), [groups]);
  const isWithin = (code, ancestor) => {
    for (let g = groupByCode[code]; g; g = g.parent ? groupByCode[g.parent] : null) if (g.code === ancestor) return true;
    return false;
  };

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return ledgers.filter(
      (l) =>
        (includeInactive || l.active) &&
        (!group || isWithin(l.group, group)) &&
        (!kind || kindOf(l) === kind) &&
        (!needle || l.name.toLowerCase().includes(needle) || l.groupLabel.toLowerCase().includes(needle))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledgers, group, kind, search, includeInactive, groupByCode]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (ledger) => {
    setEditing(ledger);
    setForm({
      name: ledger.name || "",
      group: ledger.group,
      openingBalance: Number(ledger.openingBalance) ? String(ledger.openingBalance) : "",
      openingBalanceSide: ledger.openingBalanceSide || "DR",
      gstin: ledger.gstin || "",
      creditDays: ledger.creditDays ?? "",
      bankName: ledger.bankName || "",
      bankAccountNumber: ledger.bankAccountNumber || "",
      ifscCode: ledger.ifscCode || "",
      description: ledger.description || "",
      active: ledger.active,
    });
    setShowModal(true);
  };

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const selectedGroup = groupByCode[form.group];
  const isProfitAndLoss = selectedGroup && (selectedGroup.nature === "INCOME" || selectedGroup.nature === "EXPENSE");
  const groupLocked = Boolean(editing && (editing.systemKey || editing.partyType));

  const onGroupChange = (e) => {
    const next = groupByCode[e.target.value];
    setForm((prev) => ({
      ...prev,
      group: e.target.value,
      openingBalanceSide: next && (next.nature === "LIABILITY" || next.nature === "INCOME") ? "CR" : "DR",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        group: form.group,
        openingBalance: isProfitAndLoss || form.openingBalance === "" ? 0 : Number(form.openingBalance),
        openingBalanceSide: form.openingBalanceSide,
        gstin: form.gstin.trim() || null,
        creditDays: form.creditDays === "" ? null : Number(form.creditDays),
        bankName: form.bankName.trim() || null,
        bankAccountNumber: form.bankAccountNumber.trim() || null,
        ifscCode: form.ifscCode.trim() || null,
        description: form.description.trim() || null,
        active: form.active,
      };
      if (editing) {
        await api.put(`${ACCOUNTING}/ledgers/${editing.id}`, payload);
        toast.success("Ledger updated");
      } else {
        await api.post(`${ACCOUNTING}/ledgers`, payload);
        toast.success("Ledger created");
      }
      setShowModal(false);
      load();
    } catch (error) {
      toast.error(error.message || "Failed to save ledger");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ledger) => {
    if (!window.confirm(`Delete ledger "${ledger.name}"?`)) return;
    try {
      await api.del(`${ACCOUNTING}/ledgers/${ledger.id}`);
      toast.success("Ledger deleted");
      load();
    } catch (error) {
      toast.error(error.message || "Failed to delete ledger");
    }
  };

  const exportExcel = () =>
    download("Ledgers", [
      {
        name: "Ledgers",
        rows: [
          ...sheetHeading("Ledgers (Chart of Accounts)", `${rows.length} ledgers`),
          ...tableRows(
            [
              { label: "Ledger", key: "name" },
              { label: "Group", key: "groupLabel" },
              { label: "Nature", key: "nature" },
              { label: "Type", value: kindLabel },
              { label: "Opening Balance", key: "openingBalance", format: "money", total: false },
              { label: "Dr/Cr", value: (l) => (Number(l.openingBalance) ? l.openingBalanceSide : "") },
              { label: "GSTIN", key: "gstin" },
              { label: "Credit Days", key: "creditDays", format: "int" },
              { label: "Bank", key: "bankName" },
              { label: "Account No", key: "bankAccountNumber" },
              { label: "IFSC", key: "ifscCode" },
              { label: "Status", value: (l) => (l.active ? "Active" : "Inactive") },
            ],
            rows
          ),
        ],
      },
    ]);

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} label="Loading ledgers..." />
      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Every account the books post to. Customers, suppliers, cash, bank, sales, purchase and GST ledgers are created automatically — add ledgers
        here for expenses (rent, salaries, fuel), other incomes, capital, loans, fixed assets and additional bank accounts.
      </div>
      <div className="filters-bar">
        <button className="primary" onClick={openCreate}>
          + New Ledger
        </button>
        <select value={group} onChange={(e) => setGroup(e.target.value)} aria-label="Group">
          <option value="">All groups</option>
          {groups
            .filter((g) => g.selectable)
            .map((g) => (
              <option key={g.code} value={g.code}>
                {g.parent ? "— " : ""}
                {g.label}
              </option>
            ))}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Ledger type">
          {KINDS.map((k) => (
            <option key={k.key} value={k.key}>
              {k.label}
            </option>
          ))}
        </select>
        <input type="text" placeholder="Search ledger" value={search} onChange={(e) => setSearch(e.target.value)} />
        <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
          Show inactive
        </label>
        <ExportButton onClick={exportExcel} disabled={!rows.length} />
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Ledger</th>
              <th style={{ textAlign: "left" }}>Group</th>
              <th>Type</th>
              <th style={cellRight}>Opening Balance</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <EmptyState>No ledgers match the filters</EmptyState>
                </td>
              </tr>
            ) : (
              rows.map((ledger) => (
                <tr key={ledger.id}>
                  <td style={{ textAlign: "left" }}>
                    {ledger.name}
                    {ledger.bankAccountNumber ? <div className="page-subtitle">{[ledger.bankName, ledger.bankAccountNumber].filter(Boolean).join(" · ")}</div> : null}
                  </td>
                  <td style={{ textAlign: "left" }}>{ledger.groupLabel}</td>
                  <td>
                    <span className={`badge ${ledger.systemKey ? "badge-blue" : ledger.partyType ? "badge-amber" : "badge-gray"}`}>{kindLabel(ledger)}</span>
                  </td>
                  <td style={cellRight}>{Number(ledger.openingBalance) ? `${money(ledger.openingBalance)} ${ledger.openingBalanceSide}` : "-"}</td>
                  <td>
                    <span className={`badge ${ledger.active ? "badge-green" : "badge-gray"}`}>{ledger.active ? "Active" : "Inactive"}</span>
                  </td>
                  <td className="action-buttons">
                    <button className="secondary" onClick={() => openEdit(ledger)}>
                      Edit
                    </button>
                    {!ledger.systemKey && !ledger.partyType ? (
                      <button className="secondary" onClick={() => remove(ledger)}>
                        Delete
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editing ? "Edit Ledger" : "New Ledger"}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Ledger Name</label>
                  <input value={form.name} onChange={set("name")} required maxLength={150} />
                </div>
                <div className="form-group">
                  <label>Under Group{groupLocked ? " (fixed for system / party ledgers)" : ""}</label>
                  <select value={form.group} onChange={onGroupChange} required disabled={groupLocked}>
                    <option value="">Select group</option>
                    {groups
                      .filter((g) => g.selectable)
                      .map((g) => (
                        <option key={g.code} value={g.code}>
                          {g.parent ? "— " : ""}
                          {g.label}
                        </option>
                      ))}
                  </select>
                </div>
                {!isProfitAndLoss ? (
                  <>
                    <div className="form-group">
                      <label>Opening Balance (as at books beginning)</label>
                      <input type="number" min="0" step="0.01" value={form.openingBalance} onChange={set("openingBalance")} />
                    </div>
                    <div className="form-group">
                      <label>Dr / Cr</label>
                      <select value={form.openingBalanceSide} onChange={set("openingBalanceSide")}>
                        <option value="DR">Dr (asset / amount receivable)</option>
                        <option value="CR">Cr (liability / amount payable)</option>
                      </select>
                    </div>
                  </>
                ) : null}
                {BANK_GROUPS.includes(form.group) ? (
                  <>
                    <div className="form-group">
                      <label>Bank Name</label>
                      <input value={form.bankName} onChange={set("bankName")} maxLength={150} />
                    </div>
                    <div className="form-group">
                      <label>Account Number</label>
                      <input value={form.bankAccountNumber} onChange={set("bankAccountNumber")} maxLength={40} />
                    </div>
                    <div className="form-group">
                      <label>IFSC Code</label>
                      <input value={form.ifscCode} onChange={set("ifscCode")} maxLength={20} />
                    </div>
                  </>
                ) : null}
                {form.group === "SUNDRY_CREDITORS" || form.group === "SUNDRY_DEBTORS" ? (
                  <>
                    <div className="form-group">
                      <label>{form.group === "SUNDRY_CREDITORS" ? "Credit Days (purchase bills fall overdue after this)" : "Credit Days"}</label>
                      <input type="number" min="0" step="1" value={form.creditDays} onChange={set("creditDays")} />
                    </div>
                    <div className="form-group">
                      <label>GSTIN</label>
                      <input value={form.gstin} onChange={set("gstin")} maxLength={20} />
                    </div>
                  </>
                ) : null}
                <div className="form-group">
                  <label>Description</label>
                  <input value={form.description} onChange={set("description")} maxLength={500} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.active ? "true" : "false"} onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.value === "true" }))}>
                    <option value="true">Active</option>
                    <option value="false">Inactive (hidden from voucher entry)</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editing ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Ledgers;
